import * as React from 'react';
import styles from './style.module.scss';
import SvgEditService, { useSvgEditService } from '@/services/svg-edit-service';
import Units from '@/common/pdUnits';
import { useCurrentDpi } from '@/hooks';

interface Props {
    id?: string;
    className?: string;
    tag?: string;
    min?: number | string;
    max?: number | string;
    step?: number | string;
    title?: string;
    suffix?: string;
    label?: string;
    childrenIsLabel?: boolean;
    src?: string;
    decimalPlaces?: Integer;
    value: number | string;
    onChange: (e: number) => void;
    short?: boolean;
    dontAutoconvertUnits?: boolean;
    hidden?: boolean;
    disabled?: boolean;
    children?: any;
}

const svgEditService = SvgEditService.Get(null, () => InputUnits.UpdateAllInputs());


const parsePropsValue = (props: Props) => {
    const currentUnits = svgEditService.canvas?.getBaseUnit() ?? "mm";
    let value: string | number = Number(props?.value ?? 0);

    if (!props.dontAutoconvertUnits) {
        value = Units.convertSvgUnitsToDpiAware(value, currentUnits);
    }

    if (props?.decimalPlaces && props.decimalPlaces >= 0) {
        value = value.toFixed(props.decimalPlaces);
    }
    return value;
}


export const InputUnits = (props: Props) => {
    const svgEditService = useSvgEditService();
    const dpi = useCurrentDpi()

    const [value, setValue] = React.useState(parsePropsValue(props));
    const [lastPropsValue, setLastPropsValue] = React.useState(props.value);
    const [isFocussed, setIsFocussed] = React.useState(false);

    React.useEffect(() => {
        setValue(parsePropsValue(props));
        setLastPropsValue(props.value);
    }, [props.value, dpi]);

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key.toLowerCase().indexOf("enter") >= 0) {
            commitCurrentValue();
        }
    }

    const onFocussed = () => {
        setIsFocussed(true);
        setLastPropsValue(props.value);
    }

    const onBlurred = () => {
        setIsFocussed(false);
        if (lastPropsValue != props.value) {
            // Our context has changed from underneath us, act as if this is newly selected
            setValue(parsePropsValue(props));
            setLastPropsValue(props.value);
        } else {
            commitCurrentValue();
        }
    }

    const commitCurrentValue = () => {
        const numValue = Number(value);
        let adjustedValue = isNaN(numValue) ? 0.0 : numValue;
        if (!props.dontAutoconvertUnits) {
            adjustedValue = Units.convertToSvgUnitsDpiAware(adjustedValue, svgEditService.canvas?.getBaseUnit() ?? "mm");
        }

        setLastPropsValue(adjustedValue);
        props.onChange(adjustedValue);
    }

    const currentUnits = props.suffix ?? svgEditService.canvas?.getBaseUnit() ?? "mm";
    const hideSuffix = props.dontAutoconvertUnits && !props.suffix;

    return (
        <div className={`input-group ${styles.control} ${props.className ?? ""}`} hidden={props.hidden}>
            {props.src && (
                <img className={styles.icon} src={props.src} alt="icon" width="24" height="24" aria-labelledby="label" title={props.title} />
            )}
            {props.childrenIsLabel && props.children}
            {props.label && (
                <label htmlFor={props.id} className={`input-group-text`} title={props.title}>{props.label}</label>
            )}
            <input type="number" id={props.id} className={`form-control ${props.short ? styles.short : ""}`}
                min={props.min} max={props.max} step={props.step} title={props.title}
                value={value}
                disabled={props.disabled}
                onChange={e => setValue(e.currentTarget.value)}
                onKeyDown={onKeyDown}
                onFocus={onFocussed}
                onBlur={onBlurred}
            />
            {!hideSuffix && (
                <span className="input-group-text" title={props.title}>{currentUnits}</span>
            )}
            {!props.childrenIsLabel && props.children}
        </div>
    );
}

InputUnits.UpdateAllInputs = () => { }
